// src/features/executive/insight/widgets/CategoricalDonut.tsx
import { Box, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip as ChartTooltip,
} from 'chart.js'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { formatCount, formatVariance } from 'utils/executive.utils'
import type { CategoricalSegment } from 'api/executive/executive.types'

ChartJS.register(ArcElement, ChartTooltip, Legend)

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export interface CategoricalDonutProps {
  title: string
  segments: CategoricalSegment[]
  total: number
  colorMap: Record<string, string>  // key → hex colour
  centerLabel?: string              // e.g. "patients" localised label
  unit?: string                     // e.g. "ราย" for count formatter
  // Tooltip text shown when hovering an info icon next to the title.
  // Used to disambiguate widgets whose label might mislead readers about
  // the underlying data source (e.g. nationality vs country of residence).
  titleTooltip?: string
  // Custom EmptyState message — falls back to the default "no data" copy.
  emptyMessage?: string
  // Optional notice block (e.g. warning chip) rendered above the chart.
  notice?: ReactNode
  // Same-duration previous-window total. When provided, renders a small
  // ±X% caption under the centre count.
  previousTotal?: number | null
  loading: boolean
  error: Error | null
  onRetry: () => void
}

export default function CategoricalDonut({
  title,
  segments,
  total,
  colorMap,
  centerLabel,
  unit,
  titleTooltip,
  emptyMessage,
  notice,
  previousTotal,
  loading,
  error,
  onRetry,
}: CategoricalDonutProps) {
  const chartData = useMemo(() => {
    if (!segments.length) return null
    return {
      labels: segments.map((s) => s.label),
      datasets: [
        {
          data: segments.map((s) => s.count),
          backgroundColor: segments.map((s) => colorMap[s.key] ?? '#95a5a6'),
        },
      ],
    }
  }, [segments, colorMap])

  const isEmpty = total === 0 || !chartData
  const totalVariance = varianceOf(total, previousTotal)

  return (
    <WidgetCard
      title={title}
      titleTooltip={titleTooltip}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={emptyMessage}
      onRetry={onRetry}
      skeletonHeight={300}
    >
      <Box sx={{ height: 160, position: 'relative', mb: 2 }}>
        {chartData && (
          <Doughnut
            data={chartData as any}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              cutout: '70%',
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const value = Number(ctx.parsed)
                      const pct = total > 0 ? (value / total) * 100 : 0
                      return `${ctx.label}: ${formatCount(value, unit ?? '')} (${pct.toFixed(1)}%)`
                    },
                  },
                },
              },
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {centerLabel && (
            <Typography variant="caption" color="text.secondary">
              {centerLabel}
            </Typography>
          )}
          <Typography variant="h6">{formatCount(total, unit ?? '')}</Typography>
          {totalVariance != null && (
            <Typography
              variant="caption"
              color={totalVariance >= 0 ? 'success.main' : 'error.main'}
            >
              {formatVariance(totalVariance)}
            </Typography>
          )}
        </Box>
      </Box>
      <Stack spacing={0.5}>
        {segments.map((s) => {
          const variance = varianceOf(s.count, s.previousCount)
          return (
            <Box
              key={s.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: colorMap[s.key] ?? '#95a5a6',
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {s.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {s.count} ({s.percent.toFixed(1)}%)
              </Typography>
              {variance != null && (
                <Typography
                  variant="caption"
                  color={variance >= 0 ? 'success.main' : 'error.main'}
                  sx={{ minWidth: 56, textAlign: 'right' }}
                >
                  {formatVariance(variance)}
                </Typography>
              )}
            </Box>
          )
        })}
      </Stack>
    </WidgetCard>
  )
}
